import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaUser, FaArrowLeft, FaMapMarkerAlt, FaPhone, FaEnvelope, FaStar, FaVenusMars, FaIdCard } from 'react-icons/fa';

function ApplicantDetailsPage() {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiClient.get(`/profiles/details/${applicantId}`);
        setProfile(response.data);
      } catch (err) {
        setError('Failed to load applicant details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [applicantId]);

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;
  if (!profile) return <div className="text-center mt-10">Applicant not found.</div>;

  const { User: userData, worker_details, received_ratings } = profile;
  const averageRating = received_ratings?.length 
    ? (received_ratings.reduce((acc, curr) => acc + curr.rating, 0) / received_ratings.length).toFixed(1)
    : 'N/A';

  const formatAddress = (location) => {
      if (!location) return 'Address not available';
      if (typeof location === 'string') return location;
      const parts = [];
      if (location.upazila) parts.push(location.upazila);
      if (location.district) parts.push(location.district);
      if (location.division) parts.push(location.division);
      return parts.length > 0 ? parts.join(', ') : 'Address not specified';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={() => navigate(-1)} className="flex items-center text-blue-600 hover:underline mb-6">
        <FaArrowLeft className="mr-2" />
        Back
      </button>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-green-600 h-32"></div>
        <div className="px-8 pb-8">
          <div className="relative flex justify-between items-end -mt-12 mb-6">
            <div className="bg-white p-1 rounded-full">
                {profile.picture ? (
                    <img src={loadImage(profile.picture)} alt={profile.name} className="w-24 h-24 rounded-full object-cover border-4 border-white" />
                ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border-4 border-white">
                        <FaUser size={40} />
                    </div>
                )}
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">{profile.name || 'Unknown Name'}</h1>
          <div className="flex flex-wrap gap-4 text-gray-600 mb-6">
            <div className="flex items-center"><FaMapMarkerAlt className="mr-2" /> {formatAddress(profile.location)}</div>
            {userData?.contact_No && <div className="flex items-center"><FaPhone className="mr-2" /> {userData.contact_No}</div>}
            {userData?.email && <div className="flex items-center"><FaEnvelope className="mr-2" /> {userData.email}</div>}
            {profile.gender && <div className="flex items-center"><FaVenusMars className="mr-2" /> {profile.gender}</div>}
            {profile.nid_or_passport && <div className="flex items-center"><FaIdCard className="mr-2" /> NID: {profile.nid_or_passport}</div>}
            <div className="flex items-center text-yellow-600"><FaStar className="mr-2" /> {averageRating} ({received_ratings?.length || 0} reviews)</div>
          </div>

          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Worker Details</h2>
            {worker_details ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-medium text-gray-500 mb-1">Skills</h3>
                        <div className="flex flex-wrap gap-2">
                            {worker_details.categories?.map((cat, idx) => (
                                <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{cat.Name}</span>
                            )) || 'No skills listed'}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-medium text-gray-500 mb-1">Experience</h3>
                        <p>{worker_details.experience_year ? `${worker_details.experience_year} Years` : 'Not specified'}</p>
                    </div>
                    <div>
                        <h3 className="font-medium text-gray-500 mb-1">Strength</h3>
                        <p>{worker_details.strength || 'Not specified'}</p>
                    </div>
                     <div>
                        <h3 className="font-medium text-gray-500 mb-1">Bio</h3>
                        <p>{worker_details.bio || 'No bio available'}</p>
                    </div>
                </div>
            ) : (
                <p className="text-gray-500">No worker details available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApplicantDetailsPage;
